import { authClient } from "./auth-client";

const SESSION_URL = "/api/v1/admin/session";

type AdminStatus = "unknown" | "authed" | "anon";

/**
 * Cookie-backed admin session. better-auth keeps the session server-side; this
 * store only remembers what the last check said so the router can gate pages.
 */
class AdminSession {
  status = $state<AdminStatus>("unknown");
  actorId = $state("");
  isOwner = $state(false);
  private pending: Promise<void> | null = null;

  get authenticated() {
    return this.status === "authed";
  }

  /** Resolve the session once per page load so guards can await it. */
  ensureLoaded(): Promise<void> {
    if (!this.pending) this.pending = this.refresh();
    return this.pending;
  }

  async refresh(): Promise<void> {
    try {
      const response = await fetch(SESSION_URL, { headers: { Accept: "application/json" } });
      const payload = (await response.json().catch(() => null)) as {
        success?: boolean;
        data?: { authenticated?: boolean; actorId?: string; isOwner?: boolean };
      } | null;
      if (response.ok && payload?.success && payload.data?.authenticated) {
        this.actorId = payload.data.actorId ?? "";
        this.isOwner = payload.data.isOwner === true;
        this.status = "authed";
        return;
      }
    } catch {
      // Offline or server unreachable: treat as signed out.
    }
    this.actorId = "";
    this.isOwner = false;
    this.status = "anon";
  }

  clear() {
    this.actorId = "";
    this.isOwner = false;
    this.status = "anon";
    this.pending = Promise.resolve();
  }

  async signOut() {
    try {
      await authClient.signOut();
    } catch {
      // Removing the server session is best-effort; local state clears either way.
    }
    this.clear();
  }
}

export const adminSession = new AdminSession();
